/** The org console's own URL space.
 *
 *  An organization is never a guest on a muufree URL: every screen its
 *  staff can reach lives under `/o/<slug>/…`, the same base the branded
 *  sign-in page uses. The slug is display only — the backend scopes every
 *  request to the caller's own org — so a wrong or foreign slug is a
 *  redirect, never an access decision.
 */

/** The org surfaces, in sidebar order. `reports` is the shared posture
 *  list; the backend scopes it. */
export const ORG_PAGES = [
  'dashboard', 'people', 'reports', 'pool', 'activity', 'support', 'settings'
] as const;

export type OrgPage = typeof ORG_PAGES[number];

export function orgUrl(slug: string, page: OrgPage = 'dashboard'): string {
  return `/o/${slug}/${page}`;
}

/** The base itself, with no page on it: the address an organization
 *  hands to its own staff, and where the branded sign-in lives. */
export function orgBase(slug: string): string {
  return `/o/${slug}`;
}

/** The org page a URL is asking for, whichever base it used: the current
 *  `/o/<slug>/pool` or the retired `/org/pool` that old links and
 *  bookmarks still carry. Null when the URL is not an org surface. */
export function orgPageOf(url: string): OrgPage | null {
  const parts = url.replace(/^\//, '').split('?')[0].split('/');
  const page =
    parts[0] === 'o' ? parts[2] : parts[0] === 'org' ? parts[1] : undefined;
  return ORG_PAGES.includes(page as OrgPage) ? (page as OrgPage) : null;
}

/** The slug an `/o/<slug>/…` URL names, else null. */
export function slugOf(url: string): string | null {
  const parts = url.replace(/^\//, '').split('?')[0].split('/');
  return parts[0] === 'o' && parts[1] ? parts[1] : null;
}
